import { orderBy } from "lodash";
import i18n from "@/plugins/i18n";

//#endregion

/**
 * @class Countries
 * @description Class used for managing country collection.
 * @author Allan A. Chua
 * @version 1.0
 * @since November 30, 2018
 */
export default class Countries {
  get internalData() {
    return i18n.t("$maps.countries");
  }

  /**
   * @function getData
   * @description Method used for retrieving country objects.
   *
   * @return {Object[]} List of countries.
   */
  getData() {
    return this.internalData.slice(0);
  }

  /**
   * Returns list of countries as key-value pairs
   * @returns {{text: name, value: alpha3Code }[]}
   */
  getKeyValues() {
    const countryListSorted = orderBy(this.internalData, ["name"], ["asc"]);
    return countryListSorted.map((countryDetails) => {
      let text = countryDetails.name;
      let value = countryDetails.alpha3Code;
      return { text, value };
    });
  }

  /**
   * Find country by 3 letter country code
   * @param alpha3Code
   * @returns {{text: name, value: alpha3Code }[]}
   */
  getCountryByKey(alpha3Code) {
    return this.getKeyValues().find((element) => {
      return element.value === alpha3Code;
    });
  }

  getCountryAllDetailsByKey(alpha3Code) {
    return this.getData().find((element) => {
      return element.alpha3Code === alpha3Code;
    });
  }

  /**
   * Find country by country name
   * @param name
   * @returns {{text: name, value: alpha3Code }[]}
   */
  getCountryByName(name) {
    return this.getKeyValues().find((element) => {
      return element.text === name;
    });
  }

  /**
   * @function getNames
   * @description Method used for retrieving country names.
   *
   * @return {String[]} List of country names.
   */
  getNames() {
    return this.internalData.map((country) => country.name).sort();
  }

  getNameTextValues() {
    return this.internalData
      .map((country) => {
        return {
          text: country.name,
          value: country.value,
        };
      })
      .sort((val_a, val_b) => val_a.value - val_b.value);
  }

  /**
   * @function getNationalities
   * @description Method used for retrieving nationalities.
   *
   * @return {String[]} List of nationalities.
   */
  getNationalities() {
    const uniqueNationalities = [...new Set(this.internalData.map((country) => country.demonym))];
    return uniqueNationalities.filter((nationality) => nationality).sort();
  }

  renderCallingCodeToCountryList() {
    return this.internalData.map((countryDetails) => {
      let phoneCode = `${countryDetails.callingCodes[0]}`;
      let country = countryDetails.name;
      let description = `+${phoneCode} (${country})`;
      return { phoneCode, country, description };
    });
  }
}
